/**
 * What the model's confirmation of an operation does, for every reason the web hands an
 * operation of its list back (`EditOperationReask`, `TableOperationReask`).
 *
 * The core keeps every operation it was re-asked about (`AskedOnceLedger`). When the
 * model confirms (its yes sends the answer handed back again, or it sends the operation
 * asked about again unchanged), the core posts it confirmed
 * (`confirmedOperationIndexes`), and the web never asks about it again
 * (`applyOperationList`): it is written as given, or, where nothing as given exists
 * to write, left out while the rest of the list is written. The web says what
 * confirming does; the core, which knows what the model answers with, says how.
 *
 * Callers: the reason tables of `edit-operations.ts` and `table-operations.ts`, and
 * `applyOperationList`, which ends each re-ask with the policy's `howToConfirm`.
 */
import { z } from "zod";

/**
 * How a confirmed resend of one reason is answered.
 *
 * CASES
 *   - writtenAsGiven -> the operation can be written exactly as the model sent it, and the
 *                       check only judges it likely unmeant (two words glued together, a
 *                       doubled space): the check lets a confirmed operation through
 *   - leftOut        -> the operation names a paragraph, words or a change that are not
 *                       there, or two writes with no order between them, so no "as given"
 *                       exists to write: the operation is left out, the rest of the list is
 *                       written, and the answer names it (`OperationLeftOut`)
 *
 * `howToConfirm` is the sentence a re-ask ends with when the core confirms answers; ""
 * where the check's own message already says what a resend does.
 */
export interface OperationReaskPolicy {
    confirmedResend: "writtenAsGiven" | "leftOut";
    howToConfirm: string;
}

/** The sentence a re-ask the model may confirm ends with, for a check whose message does not say it already. */
export const CONFIRMING_WRITES_IT_AS_GIVEN = " If this is what you meant, confirm it, and it is written exactly as given.";

/** The sentence a re-ask ends with when nothing as given exists to write. */
export const CONFIRMING_LEAVES_IT_OUT = " If no operation can say what you meant, confirm it: it is then left out, and the rest of your operations are written.";

/** A reason whose confirmed resend is written exactly as given. */
export const WRITTEN_AS_GIVEN = { confirmedResend: "writtenAsGiven", howToConfirm: CONFIRMING_WRITES_IT_AS_GIVEN } as const;

/** A reason whose check's own message says what a resend does, written exactly as given. */
export const WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS = { confirmedResend: "writtenAsGiven", howToConfirm: "" } as const;

/** A reason whose confirmed resend is left out of the list. */
export const LEFT_OUT = { confirmedResend: "leftOut", howToConfirm: CONFIRMING_LEAVES_IT_OUT } as const;

/**
 * The reasons of one reason table whose confirmed resend is written as given: the only
 * reasons a check may let a confirmed operation through for.
 *
 * EXAMPLE
 *   { textGluesWords: WRITTEN_AS_GIVEN, anchorMissing: LEFT_OUT } -> "textGluesWords"
 */
export type ReasonWrittenAsGiven<Policies extends Record<string, OperationReaskPolicy>> = {
    [Reason in keyof Policies]: Policies[Reason]["confirmedResend"] extends "writtenAsGiven" ? Reason : never;
}[keyof Policies];

/**
 * One operation a confirmed resend left out of the list, with the re-ask that named it:
 * `operationIndex` 1-based, the `edit_ops[N]` the model reads, and `message` written for
 * the model, why the operation could not be written.
 */
export const operationLeftOutSchema = z.object({
    operationIndex: z.number().int().positive(),
    reason: z.string(),
    message: z.string(),
});

export type OperationLeftOut = z.infer<typeof operationLeftOutSchema>;
